import { fetchAccountNumberHistory } from '@api/useQueryCalls';
import { Divider, Text, View } from '@components/primitives';
import { AntDesign } from '@expo/vector-icons';
import { useToast } from '@hooks';
import { useNavigation } from '@react-navigation/native';
import appStyles from '@root/appStyles';
import { getStore } from '@store/storeUtils';
import colors from '@theme/colors';
import { shouldShowErrorForAccountInput, showErrorOnAPI } from '@utils/utils';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, TouchableOpacity, FlatList } from 'react-native';
import logAccountSelected from './logDropDownAccount';
import styles from './styles';

type ProductApiData = {
  prefix: string;
};

type ProductConfig = {
  productOrAccountModalTitle: string;
  transaction_category_id: string;
};

type BPDropdownProps = {
  source?: string;
  productApiData?: ProductApiData;
  productConfig?: ProductConfig;
  setSelectedAccountNumber?: (val: string) => void;
  selectedAccountNumber?: string;
  sAccountInputError?: string;
  setAccountInputError?: (val: string) => void;
};

function getDropDownStyles(sAccountInputError: string) {
  return sAccountInputError.length > 0
    ? { ...styles.dropDownStyles, borderColor: colors.error.rose, borderWidth: 1 }
    : styles.dropDownStyles;
}

function getItemSeperatorComponent() {
  return () => <Divider />;
}

function getTextOrSelectedNumber(selectedAccountNumber: string | undefined, t: (key: string) => string): string {
  return selectedAccountNumber || t('bp.selectedId');
}

const BPDropdownWithAccountsAndAPI: React.FC<BPDropdownProps> = ({
  source = '',
  productApiData = { prefix: '' },
  productConfig = { productOrAccountModalTitle: '', transaction_category_id: '' },
  setSelectedAccountNumber = () => {},
  selectedAccountNumber = '',
  sAccountInputError = '',
  setAccountInputError = () => {},
}) => {
  const { t } = useTranslation('common');
  const { setLoading, setInitialData } = getStore();
  const { productOrAccountModalTitle } = productConfig;
  const { prefix } = productApiData;

  const { isLoading, isError, error, isSuccess, data, isStale, refetch } = fetchAccountNumberHistory({
    productPrefix: prefix,
  });

  const toast = useToast();
  const navigation = useNavigation();
  const [dropDownModalData, setDropDownModalData] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  // Remove ref - control with index prop

  useEffect(() => {
    if (isStale) {
      refetch();
    }
  }, []);

  useEffect(() => {
    setLoading(isLoading);
  }, [isLoading]);

  useEffect(() => {
    if (isSuccess && data) {
      setDropDownModalData(data.list);
    }
  }, [isSuccess, data]);

  useEffect(() => {
    showErrorOnAPI(isError, error, toast, t, navigation, setInitialData);
  }, [isError, error]);

  const closeBPDropDownWithAccounts = () => setIsModalOpen(false);

  // Remove useEffect - control with index prop

  return (
    <View>
      <Text
        testID="txtSelectIdHeader"
        style={[appStyles.marginTop(16), appStyles.marginBottom(4)]}
        variant="sm-normal"
        color={colors.neutral.darkMist}>
        {t('bp.selectIdHeader')}
      </Text>
      <TouchableOpacity
        testID="btnDropDownPress"
        disabled={dropDownModalData.length === 0}
        style={getDropDownStyles(sAccountInputError)}
        onPress={() => {
          setIsModalOpen(true);
        }}>
        <Text
          testID="txtSelectId"
          variant="md-normal"
          color={
            selectedAccountNumber && selectedAccountNumber.length > 0
              ? colors.neutral.charcoal
              : colors.neutral.darkMist
          }
          flex={1}>
          {getTextOrSelectedNumber(selectedAccountNumber, t)}
        </Text>
        <AntDesign testID="btnAccordianImage" name="down" size={16} color="black" onPress={() => {}} />
      </TouchableOpacity>
      {shouldShowErrorForAccountInput(sAccountInputError)}
      <Modal
        visible={isModalOpen}
        animationType="none"
        presentationStyle="overFullScreen"
        transparent={true}
        onRequestClose={closeBPDropDownWithAccounts}>
        <TouchableOpacity
          style={styles.modalBackdrop}
          activeOpacity={1}
          onPress={closeBPDropDownWithAccounts}>
          <TouchableOpacity
            style={styles.modalContent}
            activeOpacity={1}
            onPress={e => e.stopPropagation()}>
            <View style={styles.modalHeaderContainer}>
              <View style={styles.modalHeaderRow}>
                <Text variant="lg-bold">{t(productOrAccountModalTitle)}</Text>
                <TouchableOpacity onPress={closeBPDropDownWithAccounts} style={styles.modalCloseButton}>
                  <AntDesign name="close" size={20} color={colors.neutral.charcoal} />
                </TouchableOpacity>
              </View>
            </View>
            <View style={styles.modalContentContainer}>
              <FlatList
                testID="fltLstNumbers"
                contentContainerStyle={appStyles.paddingHorizontal(16)}
                ItemSeparatorComponent={getItemSeperatorComponent()}
                data={dropDownModalData}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    testID={item}
                    onPress={() => {
                      logAccountSelected(item, productConfig, source);
                      setSelectedAccountNumber(item);
                      setIsModalOpen(false);
                      setAccountInputError('');
                    }}>
                    <Text variant="md-normal" style={appStyles.marginVertical(16)}>
                      {item}
                    </Text>
                  </TouchableOpacity>
                )}
              />
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

export default BPDropdownWithAccountsAndAPI;
